import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { formatDistanceToNowStrict } from 'date-fns'

import { createConsumer } from '@rails/actioncable'
import { FilmIcon, MusicNoteIcon, PaperClipIcon, TrashIcon } from '@heroicons/react/outline'

import Loading from '@/shared/Loading'
import NotifyLevelMenu from '@/shared/NotifyLevelMenu'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import useChatAttachments, { ALL_ACCEPTED, MAX_ATTACHMENTS } from './useChatAttachments'

// One consumer for the tab, created lazily. Each chat page subscribes to its
// own CourseChatChannel on top of it.
let cableConsumer = null
const getConsumer = () => {
  if (!cableConsumer) cableConsumer = createConsumer()
  return cableConsumer
}

// Chat page at /c/:id/chat.
//
// Cohort mentions are stored as `@cohort-<db id>` but never shown that way:
// the picker and the rendered pill both use the cohort's moniker ("Cohort 3"),
// which is what students see elsewhere in the app. Tokens are inserted by the
// picker or the mention modal, so nobody needs to know the id exists.

// Keep in step with ChannelMessage::REACTION_EMOJI, which is the server's
// whitelist. A key here that the server does not know is a 422 on click.
// Keys are stored in the database and are append-only; renaming one orphans
// every reaction already sitting under the old name.
const CHAT_REACTIONS = [
  { key: 'Like',      icon: '👍' },
  { key: 'Heart',     icon: '❤️' },
  { key: 'Laugh',     icon: '😂' },
  { key: 'Celebrate', icon: '🎉' },
  { key: 'Applause',  icon: '🙌' },
  { key: 'Fire',      icon: '🔥' },
  { key: 'Hundred',   icon: '💯' },
  { key: 'MindBlown', icon: '🤯' },
  { key: 'Wow',       icon: '😮' },
  { key: 'Sad',       icon: '😢' },
  { key: 'Thinking',  icon: '🤔' },
  { key: 'Pray',      icon: '🙏' },
  { key: 'Strong',    icon: '💪' },
  { key: 'Rocket',    icon: '🚀' },
  { key: 'Seen',      icon: '👀' },
]
const REACTION_ICON = Object.fromEntries(CHAT_REACTIONS.map(r => [r.key, r.icon]))
// The ones offered as one-click buttons on the hover bar. Three, because the
// bar also carries edit, pin and delete, and a row of six emoji next to those
// stops reading as "react" and starts reading as a toolbar.
const QUICK_REACTIONS = CHAT_REACTIONS.slice(0, 3)

// Per-group caps for the inline autocomplete.
const AC_MAX_GROUPS = 3
const AC_MAX_PEOPLE = 5
// Rooms past the server's size threshold search remotely. One character is
// too broad to be worth a request; the debounce keeps a burst of typing to a
// single one.
const REMOTE_SEARCH_MIN = 2
const REMOTE_SEARCH_DEBOUNCE_MS = 200

// Same person can arrive from both the staff list and a search result.
const dedupeById = (list) => {
  const seen = new Set()
  return list.filter(m => (seen.has(m.id) ? false : seen.add(m.id)))
}

// Cap on people surfaced per section when searching the mention modal.
const MODAL_MAX_SEARCH_RESULTS = 25

// Scroll to a message already on screen and flash it. Returns false when the
// row isn't in the DOM, which is what the history walk keys off.
const flashMessage = (messageId) => {
  const el = document.getElementById(`msg-${messageId}`)
  if (!el) return false
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  el.classList.add('ring-2', 'ring-clcpurple')
  setTimeout(() => el.classList.remove('ring-2', 'ring-clcpurple'), 1500)
  return true
}

// Applies one socket event to the message list. Pure, and returns the list
// unchanged when the event is about something we don't hold, which lets the
// batch below skip a render entirely.
//
// Unaffected rows keep their object identity, which is what lets MessageRow's
// memo bail out: a reaction landing on one message must not repaint the other
// forty-nine.
const applyChatEvent = (list, data) => {
  if (data.event === 'created') {
    return list.some(m => m.id === data.message.id) ? list : [...list, data.message]
  }

  if (data.event === 'updated') {
    if (!list.some(m => m.id === data.id)) return list
    return list.map(m => (
      m.id === data.id ? { ...m, body: data.body, editedAt: data.editedAt, pinnedAt: data.pinnedAt } : m
    ))
  }

  if (data.event === 'destroyed') {
    return list.some(m => m.id === data.id) ? list.filter(m => m.id !== data.id) : list
  }

  if (data.event === 'reaction') {
    if (!list.some(m => m.id === data.id)) return list
    return list.map(m => {
      if (m.id !== data.id) return m
      // The server can't know whose reaction is whose on a shared stream, so
      // `mine` is preserved from whatever this client already believed.
      const mineByKey = Object.fromEntries((m.reactions || []).map(r => [r.key, r.mine]))
      return { ...m, reactions: data.reactions.map(r => ({ ...r, mine: !!mineByKey[r.key] })) }
    })
  }

  return list
}

// How many messages the list will hold at once. Paging back through a room
// with years in it otherwise mounts every row it has ever fetched, and a
// reader who keeps clicking is exactly the reader whose scrolling gets slow.
//
// 300 is six pages, comfortably more than fits any screen, so the trimmed end
// is always well outside the viewport when it goes.
const MAX_LOADED = 300

// Prepending older messages drops the newest ones over the cap. The list then
// no longer reaches the present, which the caller has to know about: new
// arrivals must stop being appended to a window they do not belong to.
const capAfterPrepend = (list) => (
  list.length <= MAX_LOADED
    ? { list, detached: false }
    : { list: list.slice(0, MAX_LOADED), detached: true }
)

// Appending drops the oldest instead, and "Load earlier" is how they come back.
const capAfterAppend = (list) => (
  list.length <= MAX_LOADED ? list : list.slice(list.length - MAX_LOADED)
)

// How far back a click on a reply quote will page looking for the original
// before it stops and says so. 5 pages is 250 messages.
const JUMP_MAX_PAGES = 5

// Typography shared by the composer textarea and the highlight layer painted
// behind it. Any divergence here shows up as visibly misaligned highlights,
// so both elements must use this exact string.
const COMPOSER_BOX = 'px-3 py-2 text-sm leading-6 min-h-[2.5rem] max-h-40'

const Avatar = ({ user, size = 'h-9 w-9', link = true }) => {
  const initial = (user?.displayName?.[0] || user?.username?.[0] || '?').toUpperCase()
  const inner = (
    <div className={`${size} flex-shrink-0 rounded-full overflow-hidden bg-clcpurple/10 flex items-center justify-center`}>
      {user?.imageUrl ? (
        <img src={user.imageUrl} alt='' className='block h-full w-full object-cover' />
      ) : (
        <span className='text-sm font-semibold text-clcpurple'>{initial}</span>
      )}
    </div>
  )
  if (!link || !user?.username) return inner
  return (
    <Link to={`/profiles/${user.username}`} className='hover:opacity-80 transition-opacity' aria-label={`View ${user.displayName || user.username}'s profile`}>
      {inner}
    </Link>
  )
}

const roleLabel = (member) => {
  if (member.role === 'teaching_staff') return 'Teaching Staff'
  if (member.role === 'participant') return 'Member'
  return member.cohortLabel || 'Student'
}

// Memoised: thousands of these, re-rendered on every keystroke without it.
const MemberRow = React.memo(({ member }) => {
  const body = (
    <div className='flex items-center gap-3 px-2 py-1.5 rounded-lg group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 transition-colors'>
      <Avatar user={member} size='h-8 w-8' link={false} />
      <div className='min-w-0 flex-1'>
        <div className='text-sm font-medium text-slate-900 dark:text-slate-100 truncate group-hover:text-clcpurple'>
          {member.displayName || member.username || 'Unknown'}
        </div>
        <div className='text-xs text-slate-500 truncate'>{roleLabel(member)}</div>
      </div>
    </div>
  )
  if (!member.username) return <div className='block'>{body}</div>
  return (
    <Link to={`/profiles/${member.username}`} className='group block hover:no-underline'>
      {body}
    </Link>
  )
})

const MembersSidebar = React.memo(({ members, remote = false, total = 0, className = '' }) => {
  const staff        = members.filter(m => m.role === 'teaching_staff')
  const students     = members.filter(m => m.role === 'student')
  const participants = members.filter(m => m.role === 'participant')

  const Section = ({ title, items }) => (
    items.length === 0 ? null : (
      <div>
        <h3 className='px-2 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1'>
          {title} <span className='font-normal text-slate-400'>{items.length}</span>
        </h3>
        <div className='space-y-0.5'>
          {items.map(m => <MemberRow key={`mem-${m.id}`} member={m} />)}
        </div>
      </div>
    )
  )

  return (
    <aside className={`flex flex-col gap-5 overflow-y-auto ${className}`}>
      <Section title='Staff' items={staff} />
      <Section title='Students' items={students} />
      <Section title='Here' items={participants} />
      {/* A room this size never sent its roster. Listing the handful that did
          arrive under a heading would read as "this is everyone", so it says
          the number instead and points at the one place that can search. */}
      {remote && (
        <p className='px-2 text-xs text-slate-500 dark:text-slate-400'>
          {(total - staff.length).toLocaleString()} more in this room. Type @ in the message box to find someone.
        </p>
      )}
    </aside>
  )
})

const relativeTime = (iso) => {
  if (!iso) return ''
  const diffMs = Date.now() - new Date(iso).getTime()
  if (diffMs < 60_000) return 'just now'
  return formatDistanceToNowStrict(new Date(iso), { addSuffix: true })
}

const memberSubtitle = (item) => {
  if (item.role === 'teaching_staff') return 'Teaching Staff'
  return item.cohortLabel || 'Student'
}

// One selectable @-target. Shared by the inline autocomplete and the mention
// modal so the two can't drift apart. `onPick` fires on mousedown rather than
// click so the textarea's blur handler can't close the list first.
const MentionOptionRow = ({ item, active = false, onPick, onHover }) => (
  <button
    type='button'
    onMouseDown={(e) => { e.preventDefault(); onPick(item) }}
    onMouseEnter={onHover}
    className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm ${active ? 'bg-clcpurple/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
  >
    {item.kind === 'special' ? (
      <div className='h-8 w-8 flex-shrink-0 rounded-full bg-clcpurple/20 flex items-center justify-center text-clcpurple font-bold'>@</div>
    ) : (
      <Avatar user={item} size='h-8 w-8' link={false} />
    )}
    <div className='min-w-0 flex-1'>
      <div className='font-semibold text-slate-900 dark:text-slate-100 truncate'>
        {item.kind === 'special' ? item.label : item.displayName}
      </div>
      <div className='text-xs text-slate-500 truncate'>
        {item.kind === 'special' ? item.description : memberSubtitle(item)}
      </div>
    </div>
    {item.kind !== 'special' && (
      <span className='shrink-0 ml-3 text-xs text-slate-400 dark:text-slate-500'>{item.username}</span>
    )}
  </button>
)

// Staff-only: put somebody in this room by hand, no invitation for them to
// accept. A popover rather than a modal because it is a one-field errand, and
// it stays open after each pick so adding three people is three clicks and not
// three trips.
//
// Every keystroke goes to the server. There is no local list to filter here
// the way the mention picker filters its roster: the whole point is finding
// people who are NOT in the room.
const InvitePopover = ({ open, onClose, onSearch, onInvite }) => {
  const [query, setQuery] = useState('')
  const [found, setFound] = useState([])
  const [pendingId, setPendingId] = useState(null)
  const [added, setAdded] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) return
    setQuery(''); setFound([]); setAdded([]); setError(null); setPendingId(null)
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const term = query.trim()
    if (term.length < REMOTE_SEARCH_MIN) { setFound([]); return undefined }

    const timer = setTimeout(() => onSearch(term, setFound), REMOTE_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [open, query, onSearch])

  if (!open) return null

  const pick = (user) => {
    if (pendingId) return
    setError(null)
    setPendingId(user.id)
    onInvite(user, (err) => {
      setPendingId(null)
      if (err) { setError('Could not add them. Try again.'); return }
      setAdded(prev => [user, ...prev.filter(u => u.id !== user.id)])
      // Dropped from the candidates rather than left sitting there greyed
      // out, so a second click can't be a second invite for the same person.
      setFound(prev => prev.filter(u => u.id !== user.id))
    })
  }

  const term = query.trim()
  const tooShort = term.length > 0 && term.length < REMOTE_SEARCH_MIN

  return (
    <>
      <div className='fixed inset-0 z-20' onClick={onClose} />
      <div className='absolute right-0 top-full mt-2 w-80 max-w-[90vw] rounded-lg bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 z-30'>
        <div className='px-3 py-2.5 border-b border-slate-100 dark:border-slate-700'>
          <div className='text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2'>
            Add someone to this room
          </div>
          <input
            type='text'
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
            placeholder='Username or name'
            className='w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-clcpurple'
          />
        </div>

        {error && (
          <div className='px-3 py-2 text-xs text-rose-600 dark:text-rose-400'>{error}</div>
        )}

        {added.length > 0 && (
          <div className='px-3 py-2 border-b border-slate-100 dark:border-slate-700'>
            {added.map(u => (
              <div key={`added-${u.id}`} className='flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 py-0.5'>
                <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4 text-emerald-500 shrink-0' aria-hidden='true'>
                  <path fillRule='evenodd' d='M16.704 5.29a1 1 0 010 1.415l-7.5 7.5a1 1 0 01-1.415 0l-3.5-3.5a1 1 0 111.415-1.415l2.792 2.793 6.793-6.793a1 1 0 011.415 0z' clipRule='evenodd' />
                </svg>
                <span className='truncate'>{u.displayName || u.username} is in the room</span>
              </div>
            ))}
          </div>
        )}

        <div className='max-h-72 overflow-y-auto'>
          {found.map(u => (
            <button
              key={`cand-${u.id}`}
              type='button'
              onClick={() => pick(u)}
              disabled={!!pendingId}
              className='w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 disabled:opacity-50'
            >
              <Avatar user={u} size='h-8 w-8' link={false} />
              <div className='min-w-0 flex-1'>
                <div className='font-semibold text-slate-900 dark:text-slate-100 truncate'>{u.displayName}</div>
                <div className='text-xs text-slate-500 truncate'>{u.username}</div>
              </div>
              <span className='shrink-0 ml-2 text-xs font-semibold text-clcpurple'>
                {pendingId === u.id ? 'Adding…' : 'Add'}
              </span>
            </button>
          ))}
          {found.length === 0 && (
            <div className='px-3 py-4 text-xs text-slate-500 dark:text-slate-400'>
              {tooShort
                ? `Keep typing, ${REMOTE_SEARCH_MIN} letters at least.`
                : term.length === 0
                  ? 'Start typing a username or a name.'
                  : 'Nobody by that name, or they are already in the room.'}
            </div>
          )}
        </div>

        <div className='px-3 py-2 border-t border-slate-100 dark:border-slate-700 text-[11px] leading-snug text-slate-400 dark:text-slate-500'>
          They join straight away and are not told about it, so say hello in the room.
        </div>
      </div>
    </>
  )
}

// Browsable list of everything you can @. Centred dialog on desktop, bottom
// sheet on mobile. Exists because the inline picker only helps if you already
// know to type "@", and because group tokens are otherwise undiscoverable.
const MentionHelperModal = ({ open, onClose, specials, members, remote = false, total = 0, onSearch, onPick }) => {
  const [query, setQuery] = useState('')
  const [found, setFound] = useState([])
  useEffect(() => { if (!open) { setQuery(''); setFound([]) } }, [open])

  // Same deal as the inline picker: in a room too big to have sent its roster,
  // searching has to go to the server. Staff are already here and stay
  // instant.
  useEffect(() => {
    if (!open || !remote || !onSearch) return undefined
    const term = query.trim()
    if (term.length < REMOTE_SEARCH_MIN) { setFound([]); return undefined }

    const timer = setTimeout(() => onSearch(term, setFound), REMOTE_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [open, remote, query, onSearch])

  if (!open) return null

  const q = query.trim().toLowerCase()
  const pool = remote ? dedupeById([...members, ...found]) : members
  const named = pool.filter(m => m.username)
  const matches = (m) => (m.username || '').toLowerCase().includes(q)
                      || (m.displayName || '').toLowerCase().includes(q)
  const asUsers = (list) => list.map(m => ({ kind: 'user', ...m }))

  // Everyone who is not staff, whatever their role. This used to filter on
  // 'student', which meant the modal found nobody at all in a community room,
  // where every member carries the role 'participant'.
  const staff  = named.filter(m => m.role === 'teaching_staff')
  const people = named.filter(m => m.role !== 'teaching_staff')

  // The server already applied the query in a remote room, and it matches on
  // first and last name separately as well as the pair. Re-filtering here
  // would quietly drop hits this side cannot see, so it doesn't.
  const peopleHits = remote ? people : people.filter(matches)
  const tooShort = remote && q.length > 0 && q.length < REMOTE_SEARCH_MIN
  const headcount = remote ? Math.max(total - staff.length, 0) : people.length

  // People are deliberately NOT listed by default. A popular course runs into
  // the hundreds of enrolments, and a wall of names buries the groups and
  // staff that people actually open this for. Searching reveals them.
  const sections = [
    {
      title: 'Groups',
      items: specials
        .filter(s => !q || s.token.toLowerCase().includes(q) || s.label.toLowerCase().includes(q))
        .map(s => ({ kind: 'special', ...s })),
    },
    { title: 'Teaching Staff', items: asUsers(staff.filter(m => !q || matches(m))) },
  ]
  if (q && !tooShort) {
    sections.push({ title: 'People', items: asUsers(peopleHits).slice(0, MODAL_MAX_SEARCH_RESULTS) })
  }
  const visible = sections.filter(s => s.items.length > 0)

  return (
    <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center'>
      <div className='absolute inset-0 bg-slate-900/50' onClick={onClose} />
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Mention someone'
        className='relative w-full sm:max-w-md max-h-[75vh] sm:max-h-[70vh] flex flex-col bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700'
      >
        <div className='flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800'>
          <h2 className='text-base font-bold text-slate-900 dark:text-slate-100'>Mention someone</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
            aria-label='Close'
          >
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-5 w-5' aria-hidden='true'>
              <path fillRule='evenodd' d='M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z' clipRule='evenodd' />
            </svg>
          </button>
        </div>

        <div className='px-3 py-2 border-b border-slate-100 dark:border-slate-800'>
          <input
            type='text'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={headcount > 0 ? `Search ${headcount.toLocaleString()} people…` : 'Search…'}
            autoFocus
            className='w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-clcpurple focus:border-clcpurple'
          />
        </div>

        <div className='flex-1 overflow-y-auto py-1 pb-[max(0.5rem,env(safe-area-inset-bottom))]'>
          {visible.map(section => (
            <div key={section.title}>
              <div className='px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                {section.title} <span className='font-normal text-slate-400'>{section.items.length}</span>
              </div>
              {section.items.map(item => (
                <MentionOptionRow
                  key={`mm-${item.kind}-${item.kind === 'special' ? item.token : item.id}`}
                  item={item}
                  onPick={onPick}
                />
              ))}
            </div>
          ))}
          {tooShort && (
            <div className='px-4 py-8 text-center text-sm text-slate-500'>
              Keep typing, {REMOTE_SEARCH_MIN} characters or more.
            </div>
          )}
          {!tooShort && visible.length === 0 && (
            <div className='px-4 py-8 text-center text-sm text-slate-500'>No matches.</div>
          )}
          {!q && headcount > 0 && (
            <div className='px-4 py-3 text-xs text-slate-500 border-t border-slate-100 dark:border-slate-800 mt-1'>
              Search above to mention any of the {headcount.toLocaleString()} people in this room.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Match word-start @<token>. Mirrors the server-side parser.
const MENTION_TOKEN = /(?:^|\s)(@[a-z0-9_-]{2,40})(?=\s|$|[.,!?;:])/gi

// Every @token in a body, lowercased and without the leading @.
const mentionTokensIn = (text) => {
  const out = []
  if (!text) return out
  const re = new RegExp(MENTION_TOKEN.source, MENTION_TOKEN.flags)
  let m
  while ((m = re.exec(text)) !== null) out.push(m[1].slice(1).toLowerCase())
  return out
}

// Renders a message body with styled @-mention pills.
//
// `labelForToken` maps a group token to its display label so `@cohort-47`
// reads as `@Cohort 3`. It returns null for anything it doesn't know, // usernames, and group tokens from a channel whose specials aren't loaded, // and those fall back to the raw token.
const renderBody = (text, labelForToken) => {
  if (!text) return null
  const parts = []
  let lastIdx = 0
  let m
  const re = new RegExp(MENTION_TOKEN.source, MENTION_TOKEN.flags)
  while ((m = re.exec(text)) !== null) {
    const tokenStart = m.index + m[0].indexOf('@')
    if (tokenStart > lastIdx) parts.push(text.slice(lastIdx, tokenStart))
    const raw = m[1]
    const label = labelForToken?.(raw.slice(1))
    parts.push(
      <span key={`m-${tokenStart}`} className='inline-flex items-center px-1.5 py-0.5 rounded bg-clcpurple/15 text-clcpurple-dark dark:text-clcpurple-light font-medium'>
        {label ? `@${label}` : raw}
      </span>
    )
    lastIdx = tokenStart + raw.length
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx))
  return parts
}

// One button on the hover bar. Square and icon-sized so emoji and SVG sit on
// the same grid; the emoji ones lean on scale rather than colour for feedback,
// since you cannot recolour an emoji.
const BarButton = ({ label, onClick, tone = 'default', className = '', children }) => {
  const tones = {
    default: 'text-slate-500 dark:text-slate-400 hover:text-clcpurple hover:bg-clcpurple/10',
    danger: 'text-slate-500 dark:text-slate-400 hover:text-clcpink hover:bg-clcpink/10',
    plain: 'hover:bg-clcpurple/10 hover:scale-110',
  }
  return (
    <button
      type='button'
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`h-7 w-7 inline-flex items-center justify-center rounded-md transition ${tones[tone]} ${className}`}
    >
      {children}
    </button>
  )
}

const BarDivider = () => (
  <span aria-hidden='true' className='mx-0.5 h-4 w-px bg-slate-200 dark:bg-slate-700' />
)

// Two shapes, because the trigger sits in two places and each one has to open
// away from the edge it hangs off.
//
//   bar   the smiley on the desktop hover bar, which straddles the message's
//         top edge, so the menu drops downward or it clips the message above
//   plus  the dashed + at the end of the reactions row on mobile, which sits
//         at the bottom of the message, so the menu opens upward
const ReactionPicker = ({ onPick, open, setOpen, variant = 'bar', className = '' }) => (
  <div className={`relative inline-block ${className}`}>
    {variant === 'plus' ? (
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='inline-flex items-center justify-center h-6 w-8 rounded-full border border-dashed border-slate-300 dark:border-slate-600 text-slate-400 hover:text-clcpurple hover:border-clcpurple text-sm'
        aria-label='Add reaction'
      >
        +
      </button>
    ) : (
      <BarButton label='Add reaction' onClick={() => setOpen(!open)}>
        <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
          <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.536 3.05a.75.75 0 011.036.222 3 3 0 004.999 0 .75.75 0 111.248.83 4.5 4.5 0 01-7.496 0 .75.75 0 01.213-1.051z' clipRule='evenodd' />
        </svg>
      </BarButton>
    )}
    {open && (
      <>
        <div className='fixed inset-0 z-10' onClick={() => setOpen(false)} />
        {/* Fixed 2rem columns, not grid-cols-5. That shorthand is
            repeat(5, minmax(0, 1fr)), and inside an absolutely positioned box
            (which sizes shrink-to-fit) those fr columns resolve to zero, so
            every button overflowed its cell and the emoji piled on top of each
            other. The tracks have to match the h-8 w-8 buttons. */}
        <div className={`absolute grid grid-cols-[repeat(5,2rem)] gap-0.5 p-1.5 rounded-lg bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700 z-30 ${
          variant === 'plus' ? 'bottom-full mb-1 left-0' : 'top-full mt-1 right-0'
        }`}>
          {CHAT_REACTIONS.map(r => (
            <button
              key={r.key}
              type='button'
              onClick={() => { onPick(r.key); setOpen(false) }}
              className='h-8 w-8 flex items-center justify-center rounded-md text-xl hover:bg-slate-100 dark:hover:bg-slate-700'
              title={r.key}
            >
              {r.icon}
            </button>
          ))}
        </div>
      </>
    )}
  </div>
)

// Names shown in a hover card before it falls back to "and N others".
const TOOLTIP_NAMES = 3
// Press-and-hold on a pill opens the modal. Touch devices have no hover, so
// without this the reactor list would be desktop-only.
const LONG_PRESS_MS = 450
// Hover is a sweep, not an intent. Waiting this long before fetching keeps a
// pointer crossing a row of pills from firing a request per pill.
const HOVER_INTENT_MS = 180

const reactorName = (user) => user.displayName || user.username || 'Someone'

// "Alice, Bob and Carol" / "Alice, Bob, Carol and 54 others", with the tail
// clickable so the modal is one click from the hover card.
const ReactorNames = ({ users, count, onSeeAll }) => {
  const shown = users.slice(0, TOOLTIP_NAMES).map(reactorName)
  const others = Math.max(count - shown.length, 0)
  if (shown.length === 0) return <span>{count} {count === 1 ? 'person' : 'people'}</span>

  const tail = others > 0
    ? (
      <button
        type='button'
        onClick={onSeeAll}
        className='text-clcpurple-light hover:underline font-medium'
      >
        {others} {others === 1 ? 'other' : 'others'}
      </button>
      )
    : null

  return (
    <span>
      {shown.map((name, i) => (
        <span key={`rn-${i}`}>
          {i > 0 && (i === shown.length - 1 && !tail ? ' and ' : ', ')}
          {name}
        </span>
      ))}
      {tail && <> and {tail}</>}
    </span>
  )
}

// One reaction pill plus its hover card. Clicking toggles your own reaction,
// hovering (or holding, on touch) reveals who is behind the count.
const ReactionPill = ({ messageId, reaction, canReact, onToggle, fetchReactors, onSeeAll }) => {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const hoverTimer = useRef(null)
  const pressTimer = useRef(null)
  const longPressed = useRef(false)
  const alive = useRef(true)

  useEffect(() => () => {
    alive.current = false
    clearTimeout(hoverTimer.current)
    clearTimeout(pressTimer.current)
  }, [])

  const load = () => {
    fetchReactors(messageId).then((list) => {
      if (!alive.current) return
      setDetail((list || []).find(r => r.key === reaction.key) || { key: reaction.key, count: reaction.count, users: [] })
    })
  }

  const openCard = () => {
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => {
      if (!alive.current) return
      setOpen(true)
      load()
    }, HOVER_INTENT_MS)
  }

  const closeCard = () => {
    clearTimeout(hoverTimer.current)
    setOpen(false)
  }

  const seeAll = () => {
    closeCard()
    onSeeAll(messageId, reaction.key)
  }

  return (
    <div
      className='relative inline-block'
      onMouseEnter={openCard}
      onMouseLeave={closeCard}
      onFocus={openCard}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) closeCard() }}
      onKeyDown={(e) => { if (e.key === 'Escape') closeCard() }}
    >
      <button
        type='button'
        onClick={() => {
          // A long press has already opened the modal; the click that follows
          // it on touch must not also toggle the reaction.
          if (longPressed.current) { longPressed.current = false; return }
          if (canReact) onToggle(messageId, reaction.key)
        }}
        onTouchStart={() => {
          longPressed.current = false
          clearTimeout(pressTimer.current)
          pressTimer.current = setTimeout(() => { longPressed.current = true; seeAll() }, LONG_PRESS_MS)
        }}
        onTouchEnd={() => clearTimeout(pressTimer.current)}
        onTouchMove={() => clearTimeout(pressTimer.current)}
        onContextMenu={(e) => { if (longPressed.current) e.preventDefault() }}
        aria-label={`${reaction.key}, ${reaction.count}. See who reacted`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors ${
          reaction.mine
            ? 'border-clcpurple bg-clcpurple/10 text-clcnavy-dark dark:text-clcpurple-light'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
        } ${!canReact ? 'cursor-default' : ''}`}
      >
        <span>{REACTION_ICON[reaction.key] || '•'}</span>
        <span>{reaction.count}</span>
      </button>

      {open && (
        // pb-2 rather than mb-2: a margin would leave a dead gap that drops the
        // hover before the pointer reaches the card's "See all".
        <div className='absolute bottom-full left-0 z-30 pb-2 w-max max-w-[17rem]' role='tooltip'>
          <div className='rounded-lg bg-slate-900 dark:bg-slate-800 text-slate-100 shadow-xl border border-slate-700 px-3 py-2.5 flex gap-3 items-start'>
            <span className='text-2xl leading-none'>{REACTION_ICON[reaction.key] || '•'}</span>
            <div className='min-w-0 text-xs leading-5'>
              <div className='font-semibold'>{reaction.key}</div>
              <div className='text-slate-300'>
                {detail
                  ? <ReactorNames users={detail.users} count={detail.count} onSeeAll={seeAll} />
                  : 'Loading…'}
              </div>
              <button
                type='button'
                onClick={seeAll}
                className='mt-1 text-[11px] font-semibold text-clcpurple-light hover:underline'
              >
                See all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Every reaction on one message, emoji down the side and people beside it.
// Reached from a pill's hover card, or by holding a pill on touch.
const ReactionsModal = ({ open, messageId, initialKey, onClose, fetchReactors, epoch }) => {
  const [groups, setGroups] = useState(null)
  const [activeKey, setActiveKey] = useState(initialKey)

  useEffect(() => { setActiveKey(initialKey) }, [initialKey, messageId])

  useEffect(() => {
    if (!open || !messageId) return undefined
    let alive = true
    setGroups(null)
    fetchReactors(messageId).then((list) => { if (alive) setGroups(list || []) })
    return () => { alive = false }
  }, [open, messageId, epoch])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const active = groups?.find(g => g.key === activeKey) || groups?.[0] || null
  const hidden = active ? Math.max(active.count - active.users.length, 0) : 0

  return (
    <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center'>
      <div className='absolute inset-0 bg-slate-900/50' onClick={onClose} />
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Reactions'
        className='relative w-full sm:max-w-lg max-h-[75vh] sm:max-h-[70vh] flex flex-col bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700'
      >
        <div className='flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800'>
          <h2 className='text-base font-bold text-slate-900 dark:text-slate-100'>Reactions</h2>
          <button
            type='button'
            onClick={onClose}
            className='p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
            aria-label='Close'
          >
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-5 w-5' aria-hidden='true'>
              <path fillRule='evenodd' d='M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z' clipRule='evenodd' />
            </svg>
          </button>
        </div>

        {groups === null ? (
          <div className='px-4 py-10 text-center text-sm text-slate-500'>Loading…</div>
        ) : groups.length === 0 ? (
          <div className='px-4 py-10 text-center text-sm text-slate-500'>Nobody has reacted to this message.</div>
        ) : (
          // Tabs run down the left on desktop and across the top on mobile,
          // where a vertical rail would eat the width the names need.
          <div className='flex-1 min-h-0 flex flex-col sm:flex-row'>
            <div className='shrink-0 flex sm:flex-col gap-1 p-2 overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto border-b sm:border-b-0 sm:border-r border-slate-100 dark:border-slate-800 sm:w-28'>
              {groups.map(g => (
                <button
                  key={`rt-${g.key}`}
                  type='button'
                  onClick={() => setActiveKey(g.key)}
                  aria-pressed={g.key === active?.key}
                  className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    g.key === active?.key
                      ? 'bg-clcpurple/10 text-clcpurple-dark dark:text-clcpurple-light'
                      : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                  title={g.key}
                >
                  <span className='text-lg leading-none'>{REACTION_ICON[g.key] || '•'}</span>
                  <span>{g.count}</span>
                </button>
              ))}
            </div>

            <div className='flex-1 min-h-0 overflow-y-auto p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]'>
              {active?.users.map(u => (
                <Link
                  key={`ru-${u.id}`}
                  to={u.username ? `/profiles/${u.username}` : '#'}
                  onClick={(e) => { if (!u.username) e.preventDefault() }}
                  className='flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:no-underline'
                >
                  <Avatar user={u} size='h-9 w-9' link={false} />
                  <div className='min-w-0'>
                    <div className='text-sm font-semibold text-slate-900 dark:text-slate-100 truncate'>{reactorName(u)}</div>
                    {u.username && <div className='text-xs text-slate-500 truncate'>@{u.username}</div>}
                  </div>
                </Link>
              ))}
              {hidden > 0 && (
                <div className='px-2 py-3 text-xs text-slate-500'>
                  Showing the first {active.users.length} of {active.count}.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Memoised. Every prop below is stable, which is what makes it hold.
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null
  const m = Math.floor(seconds / 60)
  const sec = String(Math.floor(seconds % 60)).padStart(2, '0')
  return `${m}:${sec}`
}

const formatSize = (bytes) => {
  if (!bytes) return null
  const mb = bytes / 1024 / 1024
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

// Audio and video in a message. Native controls on purpose: they work on every
// browser and phone without a dependency, and they already know how to scrub.
//
// Nothing here touches the network until someone asks it to. A busy room can
// hold dozens of recordings, and a media element that preloads costs a request
// each on room load whether or not anyone plays it. Duration is already stored
// on the attachment, so preloading buys nothing we do not have: audio sits at
// preload='none', and a video is a poster image until it is clicked, at which
// point the real element mounts and starts playing.
//
// The URL is signed and expires, so a tab left open overnight would find its
// audio dead. Rather than refreshing every URL on a timer, this waits for the
// element to fail and asks for a fresh one then, which costs nothing for the
// overwhelming majority that are played immediately or not at all.
const MediaPlayer = ({ attachment, messageId, index }) => {
  const { getRequest } = useQuery()
  const [src, setSrc] = useState(attachment.url)
  const [expired, setExpired] = useState(false)
  const [started, setStarted] = useState(false)
  const refreshed = useRef(false)

  useEffect(() => { setSrc(attachment.url); refreshed.current = false }, [attachment.url])

  const onError = () => {
    // One retry only. A second failure is a missing object rather than a stale
    // URL, which is what a file aged out by the bucket's expiry rule looks like.
    if (!attachment.signed || refreshed.current) { setExpired(true); return }

    refreshed.current = true
    getRequest(`/api/v1/channel_messages/${messageId}/attachment_url`, { index }, (err, data) => {
      if (err || !data?.url) { setExpired(true); return }
      setSrc(data.url)
    })
  }

  const meta = [formatDuration(attachment.durationSeconds), formatSize(attachment.sizeBytes)]
    .filter(Boolean)
    .join(' · ')

  const caption = (
    <div className='mt-0.5 flex items-baseline gap-2 text-xs text-slate-500'>
      <span className='truncate'>{attachment.name}</span>
      {meta && <span className='shrink-0 text-slate-400'>{meta}</span>}
    </div>
  )

  // A blank URL means the server could not address the file at all, which is
  // what an unconfigured bucket looks like from here. There is nothing for the
  // element to fail on, so onError would never fire and the player would sit
  // there empty; say so instead.
  if (expired || !src) {
    return (
      <div className='mt-2 max-w-md rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-500'>
        {attachment.name || 'This file'} is no longer available.
      </div>
    )
  }

  if (attachment.kind === 'video' && !started) {
    return (
      <div className='mt-2 max-w-md'>
        <button
          type='button'
          onClick={() => setStarted(true)}
          className='relative block w-full overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-black'
          aria-label={`Play ${attachment.name || 'video'}`}
        >
          {/* An img can be lazy where a video element cannot: there is no
              loading attribute on <video>, and its poster is fetched as soon
              as the element renders. Off-screen videos in a long room cost
              nothing this way. */}
          {attachment.posterUrl ? (
            <img
              src={attachment.posterUrl}
              alt=''
              loading='lazy'
              className='w-full max-h-80 object-contain'
            />
          ) : (
            <div className='w-full aspect-video' />
          )}
          <span className='absolute inset-0 flex items-center justify-center'>
            <span className='flex h-14 w-14 items-center justify-center rounded-full bg-black/60 text-white'>
              <svg viewBox='0 0 24 24' fill='currentColor' className='h-7 w-7 translate-x-0.5' aria-hidden='true'>
                <path d='M8 5.14v13.72a.5.5 0 00.76.43l11.14-6.86a.5.5 0 000-.86L8.76 4.71a.5.5 0 00-.76.43z' />
              </svg>
            </span>
          </span>
          {attachment.durationSeconds != null && (
            <span className='absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white'>
              {formatDuration(attachment.durationSeconds)}
            </span>
          )}
        </button>
        {caption}
      </div>
    )
  }

  return (
    <div className='mt-2 max-w-md'>
      {attachment.kind === 'video' ? (
        <video
          src={src}
          poster={attachment.posterUrl || undefined}
          controls
          autoPlay
          playsInline
          preload='auto'
          onError={onError}
          className='w-full max-h-80 rounded-lg border border-slate-200 dark:border-slate-700 bg-black'
        />
      ) : (
        <audio src={src} controls preload='none' onError={onError} className='w-full' />
      )}
      {caption}
    </div>
  )
}

// The quoted line a reply wears above itself. Discord's shape: a short stub of
// the answered message, indented under a corner connector, clickable to go
// find the original.
//
// Deleted originals still render a stub rather than vanishing. A reply reads as
// a non-sequitur without one, and silently dropping the quote makes it look
// like the reply was always free-standing.
const ReplyQuote = ({ replyTo, onJumpTo }) => {
  if (!replyTo) return null

  const name = replyTo.displayName || replyTo.username || 'Someone'
  const stub = replyTo.body?.trim()
  const files = replyTo.attachmentCount || 0

  let text
  if (replyTo.deleted) text = 'Original message was deleted'
  else if (stub) text = stub
  else if (files > 0) text = `${files} file${files === 1 ? '' : 's'}`
  else text = 'a message'

  const inner = (
    <>
      <svg viewBox='0 0 20 20' fill='none' className='h-3 w-3 shrink-0 text-slate-400' aria-hidden='true'>
        <path d='M3 13V8a3 3 0 013-3h11' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
        <path d='M14 2.5L17.5 5 14 7.5' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' />
      </svg>
      {!replyTo.deleted && <span className='font-semibold text-slate-600 dark:text-slate-300 shrink-0'>{name}</span>}
      <span className={`truncate ${replyTo.deleted ? 'italic' : ''}`}>{text}</span>
    </>
  )

  const shared = 'mb-0.5 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 max-w-full'

  if (replyTo.deleted) return <div className={shared}>{inner}</div>

  return (
    <button
      type='button'
      onClick={() => onJumpTo(replyTo.id)}
      className={`${shared} text-left hover:text-clcpurple dark:hover:text-clcpurple-light`}
      title={`Go to ${name}'s message`}
    >
      {inner}
    </button>
  )
}

const MessageRow = React.memo(({ message, canReact, canPin, canModerate, canPost, isOwn, mentionLabelFor, highlighted, onToggleReaction, onTogglePin, onEdit, onDelete, onReply, onJumpTo, fetchReactors, onShowReactions }) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [draft, setDraft] = useState(message.body || '')
  const reactions = message.reactions || []
  const messageAttachments = message.attachments || []
  const messageImages = messageAttachments.filter(a => a.kind === 'image')
  const messageMedia = messageAttachments.filter(a => a.kind === 'audio' || a.kind === 'video')
  const canDelete = isOwn || canModerate
  const showActions = !editing && (canReact || canPin || canDelete || isOwn || canPost)
  // Mobile keeps reacting where it has always been, on the + at the end of the
  // reactions row, so the copy in the header carries moderation and replying.
  const showMobileActions = !editing && (isOwn || canPin || canDelete || canPost)

  // Built once and rendered twice, into the floating desktop bar and the
  // inline mobile one. Two copies of this list would drift apart the first
  // time an action was added to one of them.
  //
  // `compact` drops everything to do with reacting. On touch there is nothing
  // to hover, and the + at the end of the reactions row is already the place
  // people reach for; duplicating it in the header would put two ways to open
  // the same picker on one message.
  const renderActions = ({ compact }) => (
    confirmDelete ? (
      <span className='inline-flex items-center gap-2 px-1'>
        <span className='text-xs text-slate-500'>Delete?</span>
        <button type='button' onClick={() => onDelete(message.id)}
                className='text-xs font-semibold text-clcpink hover:underline'>Confirm</button>
        <button type='button' onClick={() => setConfirmDelete(false)}
                className='text-xs text-slate-400 hover:text-slate-600'>Cancel</button>
      </span>
    ) : (
      <>
        {canReact && !compact && QUICK_REACTIONS.map(r => (
          <BarButton
            key={r.key}
            label={`React with ${r.key}`}
            tone='plain'
            className='text-base leading-none'
            onClick={() => onToggleReaction(message.id, r.key)}
          >
            {r.icon}
          </BarButton>
        ))}
        {canReact && !compact && (
          <ReactionPicker
            open={pickerOpen}
            setOpen={setPickerOpen}
            onPick={(key) => onToggleReaction(message.id, key)}
          />
        )}
        {canReact && !compact && (canPin || canDelete || isOwn) && <BarDivider />}

        {/* Replying is the one action here that isn't yours or a moderator's,
            so it leads rather than sitting after the edit and delete pair. */}
        {canPost && (
          <BarButton label='Reply' onClick={() => onReply(message)}>
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
              <path fillRule='evenodd' d='M7.793 2.232a.75.75 0 01-.025 1.06L3.622 7.25h10.003a5.375 5.375 0 010 10.75H10.75a.75.75 0 010-1.5h2.875a3.875 3.875 0 000-7.75H3.622l4.146 3.957a.75.75 0 01-1.036 1.085l-5.5-5.25a.75.75 0 010-1.085l5.5-5.25a.75.75 0 011.06.025z' clipRule='evenodd' />
            </svg>
          </BarButton>
        )}
        {isOwn && (
          <BarButton label='Edit' onClick={() => { setDraft(message.body || ''); setEditing(true) }}>
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
              <path d='M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z' />
            </svg>
          </BarButton>
        )}
        {canPin && (
          <BarButton label={message.pinnedAt ? 'Unpin' : 'Pin'} onClick={() => onTogglePin(message.id)}
                     className={message.pinnedAt ? 'text-clcpurple' : ''}>
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
              <path d='M9.828 3.172a.75.75 0 011.06 0l5.94 5.94a.75.75 0 11-1.06 1.06l-.53-.53-2.22 2.22c-.164.164-.19.312-.193.398-.004.092.02.18.099.257L14.78 14.78a.75.75 0 11-1.06 1.06l-1.854-1.854a2.25 2.25 0 01-.63-1.7 2.5 2.5 0 01.62-1.49l2.13-2.13-3-3-2.13 2.13a2.5 2.5 0 01-1.49.62 2.25 2.25 0 01-1.7-.63L3.811 6.934a.75.75 0 111.06-1.06l1.265 1.265c.077.079.165.103.257.099.086-.003.234-.029.398-.193l2.22-2.22-.53-.53a.75.75 0 010-1.06zM5.47 13.47a.75.75 0 011.06 0L10 16.94l.53-.53a.75.75 0 111.06 1.06L10 19.06 5.47 14.53a.75.75 0 010-1.06z' />
            </svg>
          </BarButton>
        )}
        {/* Two-step rather than window.confirm: a native modal blocks the
            page and reads as a browser error rather than a product choice. */}
        {canDelete && (
          <BarButton label='Delete' tone='danger' onClick={() => setConfirmDelete(true)}>
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
              <path fillRule='evenodd' d='M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z' clipRule='evenodd' />
            </svg>
          </BarButton>
        )}
      </>
    )
  )

  return (
  <div
    id={`msg-${message.id}`}
    className={`group relative flex gap-3 py-3 px-2 -mx-2 rounded-md transition-colors ${
      highlighted
        ? 'bg-amber-50 dark:bg-amber-500/10 border-l-2 border-amber-400 pl-2 -ml-2'
        : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
    }`}
  >
    <Avatar user={message.user} />

    {/* Discord's pattern: the actions hang off the message's top-right edge
        and appear on hover, instead of sitting permanently in the header and
        competing with the name and timestamp for the eye.

        Touch has no hover, so below md a moderation-only copy sits in the
        header row and stays visible, and reacting stays on the + at the end
        of the reactions row. From md up this bar floats over the top edge and
        fades in with the row. */}
    {showActions && (
      <div className='absolute right-2 -top-2 z-20 hidden md:flex items-center gap-0.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-1 py-0.5 shadow-sm opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity'>
        {renderActions({ compact: false })}
      </div>
    )}

    <div className='min-w-0 flex-1'>
      <ReplyQuote replyTo={message.replyTo} onJumpTo={onJumpTo} />
      <div className='flex items-baseline gap-2 flex-wrap'>
        {message.user?.username ? (
          <Link to={`/profiles/${message.user.username}`} className='text-sm font-semibold text-clcpurple dark:text-clcpurple-light hover:underline'>
            {message.user?.displayName || message.user.username}
          </Link>
        ) : (
          <span className='text-sm font-semibold text-slate-900 dark:text-slate-100'>
            {message.user?.displayName || 'Unknown'}
          </span>
        )}
        {message.user?.username && (
          <Link to={`/profiles/${message.user.username}`} className='text-xs text-slate-500 hover:text-clcpurple'>
            @{message.user.username}
          </Link>
        )}
        <span className='text-xs text-slate-400'>{relativeTime(message.createdAt)}</span>
        {message.editedAt && <span className='text-xs text-slate-400 italic'>(edited)</span>}
        {showMobileActions && (
          <span className='order-last ml-auto flex md:hidden items-center gap-0.5 self-center'>
            {renderActions({ compact: true })}
          </span>
        )}
        {message.pinnedAt && (
          <span className='inline-flex items-center gap-1 text-xs font-semibold text-clcpurple' title='Pinned'>
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-3.5 w-3.5'>
              <path d='M9.828 3.172a.75.75 0 011.06 0l5.94 5.94a.75.75 0 11-1.06 1.06l-.53-.53-2.22 2.22c-.164.164-.19.312-.193.398-.004.092.02.18.099.257L14.78 14.78a.75.75 0 11-1.06 1.06l-1.854-1.854a2.25 2.25 0 01-.63-1.7 2.5 2.5 0 01.62-1.49l2.13-2.13-3-3-2.13 2.13a2.5 2.5 0 01-1.49.62 2.25 2.25 0 01-1.7-.63L3.811 6.934a.75.75 0 111.06-1.06l1.265 1.265c.077.079.165.103.257.099.086-.003.234-.029.398-.193l2.22-2.22-.53-.53a.75.75 0 010-1.06zM5.47 13.47a.75.75 0 011.06 0L10 16.94l.53-.53a.75.75 0 111.06 1.06L10 19.06 5.47 14.53a.75.75 0 010-1.06z' />
            </svg>
            Pinned
          </span>
        )}
      </div>
      {editing ? (
        <div className='mt-1'>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className='w-full resize-none px-3 py-2 rounded-lg border border-clcpurple bg-white dark:bg-slate-800 text-[15px] focus:outline-none focus:ring-2 focus:ring-clcpurple'
          />
          <div className='mt-1.5 flex items-center gap-2'>
            <button type='button'
                    onClick={() => { const b = draft.trim(); if (b) { onEdit(message.id, b) } setEditing(false) }}
                    className='px-3 py-1 rounded-lg bg-clcpurple text-white text-xs font-semibold hover:bg-clcpurple-dark'>Save</button>
            <button type='button' onClick={() => setEditing(false)}
                    className='px-3 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-700'>Cancel</button>
          </div>
        </div>
      ) : (
        <div className='mt-0.5 text-[15px] text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words'>
          {renderBody(message.body, mentionLabelFor)}
        </div>
      )}
      {messageImages.length > 0 && (
        <div className={`mt-2 gap-1.5 ${messageImages.length === 1 ? 'block' : 'grid grid-cols-2 max-w-md'}`}>
          {messageImages.map((att, idx) => (
            <a key={att.url} href={att.url} target='_blank' rel='noopener noreferrer' className='block'>
              <img
                src={att.url}
                alt={att.name || ''}
                loading='lazy'
                className={`rounded-lg border border-slate-200 dark:border-slate-700 ${messageImages.length === 1 ? 'max-h-80' : 'h-40 w-full object-cover'}`}
              />
            </a>
          ))}
        </div>
      )}
      {messageMedia.map(att => (
        <MediaPlayer
          key={att.url}
          attachment={att}
          messageId={message.id}
          index={messageAttachments.indexOf(att)}
        />
      ))}
      {message.linkUrl && (
        <a href={message.linkUrl} target='_blank' rel='noopener noreferrer' className='mt-2 inline-block text-sm text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark break-all'>
          {message.linkUrl}
        </a>
      )}
      {/* The + is mobile's add-reaction and desktop's is the smiley on the
          hover bar, so a message with no reactions yet has nothing to show on
          desktop and the whole row is hidden there rather than left empty. */}
      {(reactions.length > 0 || canReact) && (
        <div className={`mt-1.5 flex items-center gap-1 flex-wrap ${reactions.length === 0 ? 'md:hidden' : ''}`}>
          {reactions.map(r => (
            <ReactionPill
              key={r.key}
              messageId={message.id}
              reaction={r}
              canReact={canReact}
              onToggle={onToggleReaction}
              fetchReactors={fetchReactors}
              onSeeAll={onShowReactions}
            />
          ))}
          {canReact && (
            <ReactionPicker
              variant='plus'
              className='md:hidden'
              open={pickerOpen}
              setOpen={setPickerOpen}
              onPick={(key) => onToggleReaction(message.id, key)}
            />
          )}
        </div>
      )}


    </div>
  </div>
  )
})

// Returns { query, tokenStart } for an active @token at the caret, or null if none.
const activeMentionAt = (value, caret) => {
  if (caret < 1) return null
  // Walk backward from caret until whitespace or start-of-string or @.
  let i = caret - 1
  while (i >= 0 && !/\s/.test(value[i]) && value[i] !== '@') i--
  if (i < 0 || value[i] !== '@') return null
  // Must be preceded by whitespace or start-of-string (mirrors server parser).
  if (i > 0 && !/\s/.test(value[i - 1])) return null
  const query = value.slice(i + 1, caret)
  if (!/^[a-z0-9_-]*$/i.test(query)) return null
  return { query, tokenStart: i }
}

const CourseChat = () => {
  // Two routes reach this page: /c/:id/chat for the legacy course-wide room,
  // and /chats/:slug for cohort and community rooms. Exactly one param is set.
  const { id: courseSlug, slug: roomSlug } = useParams()
  const history = useHistory()
  const [currentCourse] = useGlobalState('currentCourse')
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  // useQuery rebuilds these each render; the ref keeps handlers stable so the
  // memoised rows below actually hold.
  const apiRef = useRef({ getRequest, putpostRequest })
  apiRef.current = { getRequest, putpostRequest }

  const [channel, setChannel] = useState(null)
  const [messages, setMessages] = useState([])
  // Mirrors `messages`, so the jump-to walk can read the oldest loaded id
  // between its own fetches without waiting for a render.
  const messagesRef = useRef([])
  messagesRef.current = messages
  // Guards the jump-to walk against re-entry. A ref rather than state: nothing
  // renders from it, and as state it changed scrollToMessage's identity, which
  // re-rendered every message row in the list.
  const jumpSearching = useRef(false)
  // Socket events waiting for the next frame, and whether anything in that
  // batch should pull a reader who is already at the bottom down with it.
  const pendingEvents = useRef([])
  const flushScheduled = useRef(false)
  const flushHandle = useRef(null)
  const followOnFlush = useRef(false)
  // The id alone, not the channel object. Depending on the object would rebuild
  // every callback that mentions it each time anything on the room changed,
  // such as its notify level.
  const channelId = channel?.id
  const [loading, setLoading] = useState(true)
  const [accessError, setAccessError] = useState(null)
  // Bumped after a join or a leave to re-run the loader, which is the only
  // thing that knows how to rebuild channel, messages and members together.
  const [reloadKey, setReloadKey] = useState(0)
  const [joinPending, setJoinPending] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)

  // Autocomplete + members list
  const [members, setMembers] = useState([])
  const [specials, setSpecials] = useState([])
  // A room past the server's threshold does not send its roster. `members`
  // then holds staff plus whatever the last search returned, and `memberTotal`
  // is the real headcount for the sidebar.
  const [membersRemote, setMembersRemote] = useState(false)
  const [memberTotal, setMemberTotal] = useState(0)
  const [remoteResults, setRemoteResults] = useState([])
  const [acOpen, setAcOpen] = useState(false)
  const [acQuery, setAcQuery] = useState('')
  const [acTokenStart, setAcTokenStart] = useState(0)
  const [acIndex, setAcIndex] = useState(0)
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false)
  const [pinnedOpen, setPinnedOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [mentionModalOpen, setMentionModalOpen] = useState(false)
  // { messageId, key } while the reactions modal is up.
  const [reactionsModal, setReactionsModal] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  // True once paging back has trimmed the newest messages away, so the list
  // no longer runs up to the present.
  const [detached, setDetached] = useState(false)
  // The socket callback is created once per room, so it reads `detached`
  // through a ref rather than closing over a stale copy.
  const detachedRef = useRef(false)
  detachedRef.current = detached
  const [loadingEarlier, setLoadingEarlier] = useState(false)

  // messageId -> promise of that message's reactor groups. Hovering the same
  // pill twice shouldn't hit the server twice, and a row of pills on one
  // message shares a single request. Entries are dropped whenever that
  // message's reactions change, below.
  const reactorsRef = useRef(new Map())
  // Bumped alongside those drops so an open modal refetches instead of sitting
  // on a list that no longer matches the counts behind it.
  const [reactorsEpoch, setReactorsEpoch] = useState(0)

  const listRef = useRef(null)
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  const { attachments, add: addAttachments, addFromDataTransfer, drop: dropAttachment,
          clear: clearAttachments, uploading: uploadingAttachments,
          payload: attachmentPayload } = useChatAttachments()
  const [dragOver, setDragOver] = useState(false)
  // The message being answered, or null. Held as the whole message rather than
  // an id so the composer bar can name the person without a second lookup.
  const [replyTarget, setReplyTarget] = useState(null)
  const overlayRef = useRef(null)
  // The shell measures itself no more: application_spa.html.erb gives <main> a
  // real height (md:h-screen) and App.jsx passes it down with flex, so the chat
  // can just claim the remaining space. This replaced both an
  // h-[calc(100vh-8rem)] guess and a getBoundingClientRect measurement, each of
  // which existed only to work around the app rendering two nested shells.
  // Caret position captured when the mention modal opens, the textarea has
  // lost focus by the time a selection comes back.
  const caretRef = useRef(null)

  // Works for both layouts: on mobile the scroll ancestor is the app's <main>;
  // on desktop it's listRef itself. scrollIntoView walks up to find it.
  const scrollToBottom = useCallback(() => {
    const el = listRef.current?.lastElementChild
    if (el) el.scrollIntoView({ block: 'end', behavior: 'auto' })
  }, [])

  // Declared up here because the socket effect below lists it as a dependency,
  // and a dependency array is evaluated during render.
  const invalidateReactors = useCallback((messageId) => {
    reactorsRef.current.delete(messageId)
    setReactorsEpoch(e => e + 1)
  }, [])

  const fetchReactors = useCallback((messageId) => {
    const cached = reactorsRef.current.get(messageId)
    if (cached) return cached

    const promise = new Promise((resolve) => {
      apiRef.current.getRequest(`/api/v1/channel_messages/${messageId}/reactors`, {}, (err, data) => {
        // A failed lookup must not stick: dropping it lets the next hover try
        // again rather than pinning a spinner there for the session.
        if (err || !data) { reactorsRef.current.delete(messageId); resolve(null); return }
        resolve(data.reactions || [])
      })
    })
    reactorsRef.current.set(messageId, promise)
    return promise
  }, [])

  const showReactions = useCallback((messageId, key) => {
    setReactionsModal({ messageId, key })
  }, [])

  const closeReactionsModal = useCallback(() => setReactionsModal(null), [])

  useEffect(() => {
    setLoading(true)
    setAccessError(null)
    const endpoint = roomSlug
      ? `/api/v1/channels/by_slug/${roomSlug}`
      : `/api/v1/channels/by_course/${courseSlug}`
    getRequest(endpoint, {}, (err, jsonData) => {
      if (err) {
        setLoading(false)
        // A locked room is an offer, not a failure, so the whole body is kept
        // rather than flattened to a message string.
        setAccessError(typeof err === 'string' ? { error: err } : (err || { error: 'could not load channel' }))
        return
      }
      const ch = jsonData.channel
      setChannel(ch)
      getRequest(`/api/v1/channels/${ch.id}/messages`, {}, (err2, data2) => {
        setLoading(false)
        if (err2) return
        setMessages(data2.messages || [])
        setHasMore(!!data2.hasMore)
        setTimeout(scrollToBottom, 50)
      })
      getRequest(`/api/v1/channels/${ch.id}/members`, {}, (err3, data3) => {
        if (err3) return
        setMembers(data3.members || [])
        setSpecials(data3.specials || [])
        setMembersRemote(!!data3.remote)
        setMemberTotal(data3.total || (data3.members || []).length)
        setRemoteResults([])
      })
    })
  }, [courseSlug, roomSlug, reloadKey])

  // Filtered autocomplete, grouped and capped PER GROUP. A single shared cap
  // with groups sorted first meant a course with enough cohorts pushed every
  // human off the list, mentioning a classmate, the common case, was the
  // thing that got starved.
  const acGroups = useMemo(() => {
    const q = acQuery.toLowerCase()
    const matchMember = (m) => {
      if (!q) return true
      return (m.username || '').toLowerCase().includes(q)
          || (m.displayName || '').toLowerCase().includes(q)
    }
    const matchSpecial = (s) => !q || s.token.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)
    const specialItems = specials.filter(matchSpecial).map(s => ({ kind: 'special', ...s })).slice(0, AC_MAX_GROUPS)

    // Staff came down with the room either way, so they stay searchable
    // locally and appear before the search results rather than after a
    // round trip.
    const pool = membersRemote ? dedupeById([...members, ...remoteResults]) : members
    const userItems = pool.filter(m => m.username).filter(matchMember).map(m => ({ kind: 'user', ...m })).slice(0, AC_MAX_PEOPLE)
    return [
      { title: 'Groups', items: specialItems },
      { title: 'People', items: userItems },
    ].filter(g => g.items.length > 0)
  }, [acQuery, members, specials, membersRemote, remoteResults])

  // Flattened in render order, keyboard nav indexes into this.
  const acItems = useMemo(() => acGroups.flatMap(g => g.items), [acGroups])

  // One place that knows how to ask the server for people, shared by the
  // inline picker and the browse-everything modal.
  const searchMembers = useCallback((term, onResults) => {
    if (!channel?.id) return

    apiRef.current.getRequest(`/api/v1/channels/${channel.id}/members`, { q: term }, (err, data) => {
      if (err) return
      onResults(data.members || [])
    })
  }, [channel])

  // Staff invite picker. A different endpoint from searchMembers on purpose:
  // that one searches inside the room, this one searches the whole site for
  // the people who are not in it yet.
  const searchInviteCandidates = useCallback((term, onResults) => {
    if (!channel?.id) return

    apiRef.current.getRequest(`/api/v1/channels/${channel.id}/invite_candidates`, { q: term }, (err, data) => {
      if (err) return
      onResults(data.members || [])
    })
  }, [channel])

  // Splices the new person into the sidebar rather than refetching the roster,
  // which in a big room would be a search request the invite already answered.
  const inviteUser = useCallback((user, done) => {
    if (!channel?.id) return

    apiRef.current.putpostRequest(
      `/api/v1/channels/${channel.id}/invite`,
      'POST',
      { user_id: user.id },
      (err, data) => {
        if (err || !data?.member) { done(err || new Error('invite failed')); return }

        setMembers(prev => dedupeById([...prev, data.member]))
        setMemberTotal(prev => prev + 1)
        done(null)
      }
    )
  }, [channel])

  // In a big room the roster never arrived, so the picker asks the server as
  // you type. Debounced, because otherwise every keystroke inside an @mention
  // is a request.
  useEffect(() => {
    if (!membersRemote || !channel?.id) return undefined
    if (!acOpen || acQuery.length < REMOTE_SEARCH_MIN) { setRemoteResults([]); return undefined }

    const timer = setTimeout(() => searchMembers(acQuery, setRemoteResults), REMOTE_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [membersRemote, acOpen, acQuery, searchMembers])

  // Group tokens render as their label ("@Cohort 3"); usernames stay as typed.
  const mentionLabelFor = useCallback((token) => {
    const hit = specials.find(s => s.token.toLowerCase() === token.toLowerCase())
    return hit ? hit.label : null
  }, [specials])

  // Every token that means "you": your username, @all, @staff when you're on
  // the teaching team, and your own cohort. Used to tint messages aimed at you.
  const myMentionTokens = useMemo(() => {
    const set = new Set()
    if (currentUser?.username) set.add(currentUser.username.toLowerCase())
    set.add('all')
    if (channel?.role === 'teaching_staff') set.add('staff')
    const me = members.find(m => m.id === currentUser?.id)
    if (me?.cohortId) set.add(`cohort-${me.cohortId}`)
    return set
  }, [currentUser, channel, members])

  // Own messages never highlight, you know you mentioned yourself.
  const mentionsMe = useCallback((message) => {
    if (!currentUser || message.user?.id === currentUser.id) return false
    return mentionTokensIn(message.body).some(t => myMentionTokens.has(t))
  }, [currentUser, myMentionTokens])

  // Once per message-list change, not per render.
  const highlightedIds = useMemo(
    () => new Set(messages.filter(mentionsMe).map(m => m.id)),
    [messages, mentionsMe]
  )

  // Tokens the composer should paint: ones that will actually resolve. A
  // typo'd or unpermitted token stays unhighlighted, which is the cue that it
  // won't ping anyone.
  const knownTokens = useMemo(() => new Set([
    ...specials.map(s => s.token.toLowerCase()),
    ...members.map(m => (m.username || '').toLowerCase()).filter(Boolean),
    ...remoteResults.map(m => (m.username || '').toLowerCase()).filter(Boolean)
  ]), [specials, members, remoteResults])

  // In a big room the roster is not here to check against, so a username we
  // have not seen is unknown-to-us rather than invalid. Painting it as a typo
  // would be wrong for most of the room, so the composer gives it the benefit
  // of the doubt and the server still has the final say on the way in.
  const isKnownToken = useCallback(
    (token) => knownTokens.has(token.toLowerCase()) || (membersRemote && !token.startsWith('cohort-')),
    [knownTokens, membersRemote]
  )

  useEffect(() => {
    if (acIndex >= acItems.length) setAcIndex(0)
  }, [acItems.length])

  // Live updates. Reaction events carry counts only, the server can't know
  // whose reaction is whose on a shared stream, so `mine` is preserved from
  // whatever this client already believed.
  useEffect(() => {
    if (!channel?.id) return undefined

    const subscription = getConsumer().subscriptions.create(
      { channel: 'CourseChatChannel', channel_id: channel.id },
      {
        received: (data) => {
          if (!data || !data.event) return

          // Someone else's reaction landed, so any reactor list we hold for
          // this message is now a name short (or long). Done on arrival rather
          // than in the flush: it only clears a cache, and it should not wait.
          if (data.event === 'reaction') invalidateReactors(data.id)

          // Whether to follow the conversation has to be answered against the
          // scroll position as it is now, before the batch lands and changes
          // the height it is measured from.
          // A message arriving while the reader is back in history has
          // nowhere to go: appending it to a window that stops months short of
          // the present would put it next to text it does not follow. The
          // jump-to-present button is how they catch up.
          if (data.event === 'created' && detachedRef.current) return

          if (data.event === 'created' && !followOnFlush.current) {
            const el = listRef.current
            followOnFlush.current = !el || (el.scrollHeight - el.scrollTop - el.clientHeight) < 120
          }

          // React 17 has no automatic batching outside its own event handlers,
          // so twenty people posting at once was twenty separate renders of the
          // room. Events are collected and applied together on the next frame.
          pendingEvents.current.push(data)
          if (flushScheduled.current) return

          flushScheduled.current = true
          flushHandle.current = requestAnimationFrame(() => {
            flushScheduled.current = false
            const batch = pendingEvents.current
            pendingEvents.current = []
            const follow = followOnFlush.current
            followOnFlush.current = false

            // Trimming the oldest off the top means there is history above the
            // window again, whether or not the server said so last time.
            if (messagesRef.current.length + batch.length > MAX_LOADED) setHasMore(true)
            setMessages(prev => capAfterAppend(batch.reduce(applyChatEvent, prev)))
            // Yanking a reader down mid-scroll is worse than a missed line, so
            // this only fires for someone already at the bottom.
            if (follow) setTimeout(scrollToBottom, 50)
          })
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      // A frame still pending when the room closes would flush into a list
      // nobody is showing any more.
      if (flushHandle.current) cancelAnimationFrame(flushHandle.current)
      flushScheduled.current = false
      pendingEvents.current = []
      followOnFlush.current = false
    }
  }, [channel?.id, scrollToBottom, invalidateReactors])

  // Escape closes the mention modal even though focus sits inside it, not on
  // the textarea whose onKeyDown handles the inline picker.
  useEffect(() => {
    if (!mentionModalOpen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setMentionModalOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mentionModalOpen])

  const onDraftChange = (e) => {
    const val = e.target.value
    setDraft(val)
    const caret = e.target.selectionStart
    const m = activeMentionAt(val, caret)
    if (m) {
      setAcQuery(m.query)
      setAcTokenStart(m.tokenStart)
      setAcOpen(true)
      setAcIndex(0)
    } else {
      setAcOpen(false)
    }
  }

  const insertMention = (item) => {
    const token = item.kind === 'special' ? item.token : item.username
    const insertText = `@${token} `
    const before = draft.slice(0, acTokenStart)
    const afterStart = acTokenStart + 1 + acQuery.length
    const after = draft.slice(afterStart)
    const next = before + insertText + after
    setDraft(next)
    setAcOpen(false)
    setAcQuery('')
    // Re-focus + place caret right after inserted text.
    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (!ta) return
      const pos = before.length + insertText.length
      ta.focus()
      ta.setSelectionRange(pos, pos)
    })
  }

  const openMentionModal = () => {
    caretRef.current = textareaRef.current?.selectionStart ?? draft.length
    setAcOpen(false)
    setMentionModalOpen(true)
  }

  // Insert from the modal, which has no active @token to replace. Guarantees a
  // whitespace boundary before the "@" because both the server parser and
  // MENTION_TOKEN only match at start-of-string or after whitespace, without
  // it, picking a name mid-word would produce a mention that never fires.
  const insertTokenAtCaret = (item) => {
    const token = item.kind === 'special' ? item.token : item.username
    const pos = Math.min(caretRef.current ?? draft.length, draft.length)
    const before = draft.slice(0, pos)
    const after = draft.slice(pos)
    const insertText = `${before.length > 0 && !/\s$/.test(before) ? ' ' : ''}@${token} `
    setDraft(before + insertText + after)
    setMentionModalOpen(false)
    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (!ta) return
      const caret = before.length + insertText.length
      ta.focus()
      ta.setSelectionRange(caret, caret)
    })
  }

  // Draft split into plain text and highlighted mention spans. The trailing
  // newline guard keeps the layer's height in step with the textarea, which
  // reserves a line for a trailing "\n" where a div would collapse it.
  const composerHighlightParts = useMemo(() => {
    const parts = []
    let lastIdx = 0
    const re = new RegExp(MENTION_TOKEN.source, MENTION_TOKEN.flags)
    let m
    while ((m = re.exec(draft)) !== null) {
      const tokenStart = m.index + m[0].indexOf('@')
      if (tokenStart > lastIdx) parts.push(draft.slice(lastIdx, tokenStart))
      const raw = m[1]
      parts.push(
        isKnownToken(raw.slice(1))
          ? <span key={`ch-${tokenStart}`} className='rounded bg-clcpurple/20'>{raw}</span>
          : raw
      )
      lastIdx = tokenStart + raw.length
    }
    if (lastIdx < draft.length) parts.push(draft.slice(lastIdx))
    if (draft.endsWith('\n')) parts.push(' ')
    return parts
  }, [draft, isKnownToken])

  // Grow with the message. rows={1} kept it one line tall, so anything longer
  // scrolled inside a slot too small to read it. max-h-40 still caps the growth,
  // and past that it scrolls as before.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft])

  const syncOverlayScroll = (e) => {
    if (overlayRef.current) overlayRef.current.scrollTop = e.target.scrollTop
  }

  // Tapping dead space in the chat drops focus into the composer, so replying
  // never needs a deliberate aim at the input. Anything interactive, and any
  // click that ends a text selection, is left alone.
  const focusComposerFromTap = (e) => {
    if (e.target.closest?.('a, button, input, textarea, select, label, [role="dialog"], [contenteditable]')) return
    if (String(window.getSelection?.() || '').length > 0) return
    textareaRef.current?.focus()
  }

  const setNotifyLevel = (level) => {
    if (!channel) return
    // Optimistic: the menu closes on click, so waiting for the round trip
    // would leave the button showing the old level for a beat.
    const previous = channel.notifyLevel
    setChannel(prev => ({ ...prev, notifyLevel: level, muted: level === 'none' }))
    putpostRequest(
      `/api/v1/channels/${channel.id}/notify_level`,
      'POST',
      { level },
      (err, data) => {
        if (err) {
          setChannel(prev => ({ ...prev, notifyLevel: previous, muted: previous === 'none' }))
          return
        }
        setChannel(prev => ({ ...prev, notifyLevel: data.channel.notifyLevel, muted: data.channel.muted }))
      }
    )
  }

  // Join by slug rather than id: the joinable 403 is the one response that
  // does not come with a loaded channel, so the slug from the URL is all
  // there is to go on.
  const joinRoom = () => {
    const slug = roomSlug || accessError?.slug
    if (!slug || joinPending) return

    setJoinPending(true)
    putpostRequest(`/api/v1/channels/by_slug/${slug}/join`, 'POST', {}, (err) => {
      setJoinPending(false)
      if (err) return
      setReloadKey(k => k + 1)
    })
  }

  // Leaving drops the membership row, not the read position, so coming back
  // lands where they left off rather than on months of unread history.
  const leaveRoom = () => {
    if (!channel || joinPending) return

    setJoinPending(true)
    putpostRequest(`/api/v1/channels/${channel.id}/membership`, 'DELETE', {}, (err) => {
      setJoinPending(false)
      if (err) return
      history.push('/chats')
    })
  }

  // Walk backwards through history. Keeps the reader's scroll position by
  // restoring the distance from the bottom, so prepending doesn't yank the
  // feed out from under them.
  const loadEarlier = () => {
    if (!channel || loadingEarlier || messages.length === 0) return
    setLoadingEarlier(true)
    const container = listRef.current
    const priorHeight = container ? container.scrollHeight : 0

    getRequest(`/api/v1/channels/${channel.id}/messages`, { before: messages[0].id }, (err, data) => {
      setLoadingEarlier(false)
      if (err) return
      const capped = capAfterPrepend([...(data.messages || []), ...messagesRef.current])
      setMessages(capped.list)
      if (capped.detached) setDetached(true)
      setHasMore(!!data.hasMore)
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - priorHeight
      })
    })
  }

  const editMessage = useCallback((messageId, body) => {
    apiRef.current.putpostRequest(
      `/api/v1/channel_messages/${messageId}`,
      'PATCH',
      { message: { body } },
      (err, data) => {
        if (err) return
        setMessages(prev => prev.map(m => (m.id === messageId ? data.message : m)))
      }
    )
  }, [])

  const deleteMessage = useCallback((messageId) => {
    apiRef.current.putpostRequest(
      `/api/v1/channel_messages/${messageId}`,
      'DELETE',
      {},
      (err) => {
        if (err) return
        setMessages(prev => prev.filter(m => m.id !== messageId))
      }
    )
  }, [])

  const toggleArchive = () => {
    if (!channel) return
    putpostRequest(
      `/api/v1/channels/${channel.id}/archive`,
      'POST',
      { archived: !channel.archivedAt },
      (err, data) => {
        if (err) return
        setChannel(prev => ({ ...prev, archivedAt: data.channel.archivedAt }))
      }
    )
  }

  const toggleReaction = useCallback((messageId, reactionKey) => {
    invalidateReactors(messageId)
    apiRef.current.putpostRequest(
      `/api/v1/channel_messages/${messageId}/toggle_reaction`,
      'POST',
      { reaction: reactionKey },
      (err, data) => {
        if (err) return
        // Again on the way back: anything fetched while the toggle was in
        // flight predates it.
        invalidateReactors(messageId)
        setMessages(prev => prev.map(m => {
          if (m.id !== messageId) return m
          const current = m.reactions || []
          if (data.count === 0) return { ...m, reactions: current.filter(r => r.key !== reactionKey) }

          // Updated in place rather than filtered-and-appended: a pill that
          // jumps to the end of the row the moment you click it makes the next
          // click land on a different emoji.
          const updated = { key: reactionKey, count: data.count, mine: data.active }
          const reactions = current.some(r => r.key === reactionKey)
            ? current.map(r => (r.key === reactionKey ? updated : r))
            : [...current, updated]
          return { ...m, reactions }
        }))
      }
    )
  }, [invalidateReactors])

  const togglePin = useCallback((messageId) => {
    apiRef.current.putpostRequest(
      `/api/v1/channel_messages/${messageId}/toggle_pin`,
      'POST',
      {},
      (err, data) => {
        if (err) return
        const updated = data.message
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, pinnedAt: updated.pinnedAt } : m))
      }
    )
  }, [])

  // Go to a message, fetching history if it isn't loaded yet.
  //
  // A reply can quote something from well before the current page, and a click
  // that silently did nothing read as a broken link. So this walks backwards a
  // page at a time until the row appears, bounded: past the cap it says the
  // message is further back rather than paging to the start of the room.
  const scrollToMessage = useCallback((messageId) => {
    setPinnedOpen(false)
    if (flashMessage(messageId)) return
    if (!channelId || jumpSearching.current) return

    jumpSearching.current = true
    let pagesLeft = JUMP_MAX_PAGES

    const giveUp = () => {
      jumpSearching.current = false
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-slate-900 dark:text-slate-100'>Message not loaded</p>
        <p className='mt-1 text-sm text-slate-500'>That one is further back. Keep loading earlier messages to reach it.</p>
      </div>)
    }

    const step = (beforeId) => {
      if (pagesLeft <= 0 || !beforeId) { giveUp(); return }
      pagesLeft -= 1

      apiRef.current.getRequest(`/api/v1/channels/${channelId}/messages`, { before: beforeId }, (err, data) => {
        if (err) { jumpSearching.current = false; return }
        const older = data.messages || []
        const container = listRef.current
        const priorHeight = container ? container.scrollHeight : 0

        const capped = capAfterPrepend([...older, ...messagesRef.current])
        setMessages(capped.list)
        if (capped.detached) setDetached(true)
        setHasMore(!!data.hasMore)

        requestAnimationFrame(() => {
          // Hold the reader's place while the page grows above them, the same
          // way loadEarlier does, or prepending yanks the feed around.
          if (container) container.scrollTop = container.scrollHeight - priorHeight
          if (flashMessage(messageId)) { jumpSearching.current = false; return }
          if (older.length === 0 || !data.hasMore) { giveUp(); return }
          step(older[0].id)
        })
      })
    }

    step(messagesRef.current[0]?.id)
  }, [channelId, setToast])

  // Back to the newest page. This is the same request the room makes when it
  // opens, so catching up needs no forward-paging endpoint: the window is
  // simply replaced with the present.
  const jumpToPresent = useCallback(() => {
    if (!channelId) return

    apiRef.current.getRequest(`/api/v1/channels/${channelId}/messages`, {}, (err, data) => {
      if (err) return
      setMessages(data.messages || [])
      setHasMore(!!data.hasMore)
      setDetached(false)
      setTimeout(scrollToBottom, 50)
    })
  }, [channelId, scrollToBottom])

  const startReply = useCallback((message) => {
    setReplyTarget(message)
    requestAnimationFrame(() => textareaRef.current?.focus())
  }, [])

  const cancelReply = useCallback(() => setReplyTarget(null), [])

  // An image on its own is a message: the server permits a body-less message
  // that carries attachments, so the only things blocked here are an entirely
  // empty composer and an upload still in flight.
  const canSend = !!channel && !sending && !uploadingAttachments &&
    (draft.trim().length > 0 || attachmentPayload.length > 0)

  const sendMessage = (e) => {
    e?.preventDefault?.()
    if (!canSend) return
    const body = draft.trim()
    const outgoing = attachmentPayload
    setSending(true)
    putpostRequest(
      `/api/v1/channels/${channel.id}/messages`,
      'POST',
      { message: { body, attachments: outgoing, reply_to_id: replyTarget?.id || null } },
      (err, jsonData) => {
        setSending(false)
        if (err) return
        // The socket has usually added this already; same guard it uses.
        setMessages(prev => (
          prev.some(m => m.id === jsonData.message.id) ? prev : [...prev, jsonData.message]
        ))
        setDraft('')
        clearAttachments()
        setReplyTarget(null)
        setAcOpen(false)
        requestAnimationFrame(() => textareaRef.current?.focus())
        setTimeout(scrollToBottom, 50)
      }
    )
  }

  const onComposerPaste = (e) => {
    if (addFromDataTransfer(e.clipboardData)) e.preventDefault()
  }

  const onComposerDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    addFromDataTransfer(e.dataTransfer)
  }

  // dragover has to be cancelled or the browser navigates to the dropped file.
  const onComposerDragOver = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return
    e.preventDefault()
    setDragOver(true)
  }

  const onFilePicked = (e) => {
    addAttachments(e.target.files)
    // Clearing the value lets the same file be picked twice in a row, which
    // otherwise fires no change event at all.
    e.target.value = ''
  }

  const handleKeyDown = (e) => {
    if (acOpen && acItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcIndex(i => (i + 1) % acItems.length); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setAcIndex(i => (i - 1 + acItems.length) % acItems.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(acItems[acIndex]); return }
      if (e.key === 'Escape')    { e.preventDefault(); setAcOpen(false); return }
    }
    // The autocomplete claims Escape first (above), so this only fires when no
    // picker is open. Cancelling the reply beats blurring the composer.
    if (e.key === 'Escape' && replyTarget) {
      e.preventDefault()
      cancelReply()
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Above the early returns: a hook that only runs on some renders changes
  // the hook count between them, which React treats as a fatal mismatch.
  const pinnedMessages = useMemo(() => messages.filter(m => m.pinnedAt), [messages])

  if (loading) return <Loading />

  if (accessError) {
    const locked = !!accessError.locked

    // A gated room the reader is entitled to. The room's own page is where
    // most people meet it, since that is where a shared link lands them, so
    // the door has to be here and not only in the chat index.
    if (accessError.joinable) {
      return (
        <div className='py-16 text-center'>
          <h1 className='text-2xl font-bold text-slate-900 dark:text-slate-100'>
            # {accessError.roomName || 'This room'}
          </h1>
          {accessError.roomSubtitle && (
            <p className='mt-1 text-sm text-slate-500'>{accessError.roomSubtitle}</p>
          )}
          <p className='mt-3 text-sm text-slate-600 dark:text-slate-400'>
            Join to read the conversation and post.
          </p>
          <button
            type='button'
            onClick={joinRoom}
            disabled={joinPending}
            className='mt-6 inline-block px-4 py-2 rounded-lg text-sm font-bold text-white bg-clcpurple hover:bg-clcpurple-dark disabled:opacity-50'
          >
            {joinPending ? 'Joining…' : 'Join this room'}
          </button>
          <div>
            <Link to='/chats' className='mt-6 inline-block text-sm font-medium text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark'>
              ← All chats
            </Link>
          </div>
        </div>
      )
    }

    return (
      <div className='py-16 text-center'>
        <h1 className='text-2xl font-bold text-slate-900 dark:text-slate-100'>
          {locked ? `${accessError.roomName || 'This room'} is for members` : 'Chat not available'}
        </h1>
        <p className='mt-2 text-sm text-slate-600 dark:text-slate-400'>
          {locked
            ? 'Closing Credits members get this room, plus the rest of the members-only chats.'
            : 'You need to be enrolled in this course or be a Closing Credits member to view this chat.'}
        </p>
        <Link
          to={locked ? '/subscriptions' : (courseSlug ? `/c/${courseSlug}` : '/chats')}
          className='mt-6 inline-block text-sm font-medium text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark'
        >
          {locked ? 'See what membership includes →' : (courseSlug ? '← Back to course' : '← All chats')}
        </Link>
      </div>
    )
  }

  const courseName = channel?.courseName || currentCourse?.name || 'Course'
  // Cohort and community rooms carry their own name; the legacy course room
  // has none and falls back to the course it belongs to.
  const roomName = channel?.name || courseName
  const backTo = channel?.courseSlug ? `/c/${channel.courseSlug}` : '/chats'
  const backLabel = channel?.courseSlug ? courseName : 'All chats'
  const isTeachingStaff = channel?.role === 'teaching_staff'
  // The headline count is the room's real size, which in a big room is not
  // the length of the list we were sent.
  const memberCount = membersRemote ? memberTotal : members.length
  const isArchived = !!channel?.archivedAt

  return (
    <div className='flex flex-col md:flex-row md:gap-6 w-full py-2 md:py-3 md:flex-1 md:min-h-0'>

    {/* Main chat column */}
    <div className='flex flex-col min-w-0 md:flex-1 md:min-h-0' onClick={focusComposerFromTap}>
      {/* Header, sticks to the top of the app's <main> scroll container on mobile,
          sits at the top of the flex column on desktop. */}
      <div className='sticky top-0 md:static z-10 bg-white dark:bg-slate-900 -mx-1 px-1 pt-1 pb-3 flex items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800'>
        <div className='min-w-0'>
          <Link to={backTo} className='text-xs font-medium text-slate-500 hover:text-clcpurple'>
            ← {backLabel}
          </Link>
          <h1 className='text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate'>
            # {roomName}
          </h1>
          {channel?.subtitle && (
            <p className='text-xs text-slate-500 dark:text-slate-400 truncate'>{channel.subtitle}</p>
          )}
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          {pinnedMessages.length > 0 && (
            <div className='relative'>
              <button
                type='button'
                onClick={() => setPinnedOpen(!pinnedOpen)}
                className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                aria-label={`${pinnedMessages.length} pinned`}
              >
                <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
                  <path d='M9.828 3.172a.75.75 0 011.06 0l5.94 5.94a.75.75 0 11-1.06 1.06l-.53-.53-2.22 2.22c-.164.164-.19.312-.193.398-.004.092.02.18.099.257L14.78 14.78a.75.75 0 11-1.06 1.06l-1.854-1.854a2.25 2.25 0 01-.63-1.7 2.5 2.5 0 01.62-1.49l2.13-2.13-3-3-2.13 2.13a2.5 2.5 0 01-1.49.62 2.25 2.25 0 01-1.7-.63L3.811 6.934a.75.75 0 111.06-1.06l1.265 1.265c.077.079.165.103.257.099.086-.003.234-.029.398-.193l2.22-2.22-.53-.53a.75.75 0 010-1.06zM5.47 13.47a.75.75 0 011.06 0L10 16.94l.53-.53a.75.75 0 111.06 1.06L10 19.06 5.47 14.53a.75.75 0 010-1.06z' />
                </svg>
                {pinnedMessages.length}
              </button>
              {pinnedOpen && (
                <>
                  <div className='fixed inset-0 z-20' onClick={() => setPinnedOpen(false)} />
                  <div className='absolute right-0 top-full mt-2 w-80 max-w-[90vw] max-h-96 overflow-y-auto rounded-lg bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 z-30'>
                    <div className='px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700'>
                      Pinned Messages
                    </div>
                    <div className='divide-y divide-slate-100 dark:divide-slate-800'>
                      {pinnedMessages.map(m => (
                        <button
                          key={`pin-${m.id}`}
                          type='button'
                          onClick={() => scrollToMessage(m.id)}
                          className='w-full text-left px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                        >
                          <div className='text-xs font-semibold text-slate-700 dark:text-slate-200 mb-0.5'>
                            {m.user?.displayName || m.user?.username || 'Unknown'}
                            <span className='ml-1.5 font-normal text-slate-400'>{relativeTime(m.createdAt)}</span>
                          </div>
                          <div className='text-xs text-slate-600 dark:text-slate-300 line-clamp-2'>
                            {m.body}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Two taps, because this sits a thumb's width from Archive on
              mobile and the two are not equally easy to undo. Rejoining is
              one click from the chat index, but only if you meant it. */}
          {channel && (
            confirmLeave ? (
              <span className='inline-flex items-center gap-1'>
                <button
                  type='button'
                  onClick={leaveRoom}
                  disabled={joinPending}
                  className='inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50'
                >
                  {joinPending ? 'Leaving…' : 'Confirm'}
                </button>
                <button
                  type='button'
                  onClick={() => setConfirmLeave(false)}
                  className='inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-700'
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type='button'
                onClick={() => setConfirmLeave(true)}
                className='inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                title='Leave this room. Your read position is kept if you come back.'
              >
                Leave
              </button>
            )
          )}
          {isTeachingStaff && !isArchived && (
            <div className='relative'>
              <button
                type='button'
                onClick={() => setInviteOpen(!inviteOpen)}
                className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
                title='Add someone to this room by username'
              >
                <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
                  <path d='M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a5 5 0 00-4.546 2.916A1.5 1.5 0 004.81 16h6.38a1.5 1.5 0 001.356-2.084A5 5 0 008 11zM16 7.75a.75.75 0 00-1.5 0V9h-1.25a.75.75 0 000 1.5h1.25v1.25a.75.75 0 001.5 0V10.5h1.25a.75.75 0 000-1.5H16V7.75z' />
                </svg>
                Add
              </button>
              <InvitePopover
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onSearch={searchInviteCandidates}
                onInvite={inviteUser}
              />
            </div>
          )}
          {isTeachingStaff && !isArchived && (
            <button
              type='button'
              onClick={toggleArchive}
              className='hidden sm:inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
              title='Archive this chat, keeps it readable but stops new messages'
            >
              Archive
            </button>
          )}
          <NotifyLevelMenu
            level={channel?.notifyLevel || 'all'}
            onChange={setNotifyLevel}
          />
          <button
            type='button'
            onClick={() => setMembersDrawerOpen(true)}
            className='md:hidden inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'
            aria-label={`Show ${memberCount} members`}
          >
            <svg viewBox='0 0 20 20' fill='currentColor' className='h-4 w-4' aria-hidden='true'>
              <path d='M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z' />
            </svg>
            {memberCount}
          </button>
        </div>
      </div>

      {/* Messages, flows naturally on mobile (main scrolls); scroll container on desktop. */}
      <div ref={listRef} className='px-1 divide-y divide-slate-100 dark:divide-slate-800 md:flex-1 md:min-h-0 md:overflow-y-auto'>
        {hasMore && (
          <div className='py-3 text-center'>
            <button
              type='button'
              onClick={loadEarlier}
              disabled={loadingEarlier}
              className='px-3 py-1.5 rounded-lg text-xs font-semibold text-clcpurple bg-clcpurple/10 hover:bg-clcpurple/20 disabled:opacity-50'
            >
              {loadingEarlier ? 'Loading…' : 'Load earlier messages'}
            </button>
          </div>
        )}
        {messages.length === 0 ? (
          <div className='h-full flex items-center justify-center text-sm text-slate-500'>
            No messages yet. Say hi!
          </div>
        ) : (
          messages.map(m => (
            <MessageRow
              key={m.id}
              message={m}
              canReact={!isArchived}
              canPin={isTeachingStaff && !isArchived}
              canModerate={isTeachingStaff && !isArchived}
              canPost={!isArchived}
              isOwn={!isArchived && m.user?.id === currentUser?.id}
              mentionLabelFor={mentionLabelFor}
              highlighted={highlightedIds.has(m.id)}
              onToggleReaction={toggleReaction}
              onTogglePin={togglePin}
              onEdit={editMessage}
              onDelete={deleteMessage}
              onReply={startReply}
              onJumpTo={scrollToMessage}
              fetchReactors={fetchReactors}
              onShowReactions={showReactions}
            />
          ))
        )}
      </div>

      {/* Composer, sticky at the bottom of the app's <main> scroll container on
          mobile, a static flex child on desktop. The browser handles keyboard
          positioning for sticky elements automatically. */}
      {/* Paging back far enough trims the newest messages away, and new
          arrivals stop landing in a window that no longer reaches them. Saying
          so is the point: a silently frozen room reads as broken. Outside the
          composer branch because an archived room can be paged back too, and
          would otherwise have no way back to the end. */}
      {detached && (
        <button
          type='button'
          onClick={jumpToPresent}
          className='sticky bottom-16 z-20 mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-clcpurple px-3 py-1.5 text-xs font-semibold text-white hover:bg-clcpurple-dark'
        >
          You're reading older messages. Jump to present
          <svg viewBox='0 0 20 20' fill='currentColor' className='h-3.5 w-3.5' aria-hidden='true'>
            <path fillRule='evenodd' d='M10 3a.75.75 0 01.75.75v9.19l3.22-3.22a.75.75 0 111.06 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 111.06-1.06l3.22 3.22V3.75A.75.75 0 0110 3z' clipRule='evenodd' />
          </svg>
        </button>
      )}
      {isArchived ? (
        <div className='sticky bottom-0 md:static mt-3 px-4 py-3 text-sm text-center text-slate-500 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
          This chat is archived. It stays readable, but nobody can post, react or pin.
          {isTeachingStaff && (
            <button type='button' onClick={toggleArchive} className='ml-2 font-semibold text-clcpurple hover:underline'>Reopen</button>
          )}
        </div>
      ) : (
        <div className='sticky bottom-0 md:relative z-10 mt-3 bg-white dark:bg-slate-900 pt-2 -mx-1 px-1 pb-[env(safe-area-inset-bottom)]'>
          {acOpen && acItems.length > 0 && (
            <div className='absolute left-0 right-0 bottom-full mb-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-10'>
              <div className='sticky top-0 px-3 py-2 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                {acQuery
                  ? <>Members matching <span className='text-clcpurple-dark dark:text-clcpurple-light'>@{acQuery}</span></>
                  : 'Members and groups'}
              </div>
              {(() => {
                let flatIdx = -1
                return acGroups.map(group => (
                  <div key={`acg-${group.title}`}>
                    <div className='px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400'>
                      {group.title}
                    </div>
                    {group.items.map(item => {
                      flatIdx += 1
                      const idx = flatIdx
                      return (
                        <MentionOptionRow
                          key={`${item.kind}-${item.kind === 'special' ? item.token : item.id}`}
                          item={item}
                          active={idx === acIndex}
                          onPick={insertMention}
                          onHover={() => setAcIndex(idx)}
                        />
                      )
                    })}
                  </div>
                ))
              })()}
            </div>
          )}

          <form onSubmit={sendMessage} className='flex gap-2 items-end'>
            <button
              type='button'
              onClick={openMentionModal}
              className='shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-300 dark:border-slate-600 text-slate-500 hover:text-clcpurple hover:border-clcpurple bg-white dark:bg-slate-800 font-bold transition-colors'
              aria-label='Mention someone'
              title='Mention someone'
            >
              @
            </button>
            <button
              type='button'
              onClick={() => fileInputRef.current?.click()}
              disabled={attachments.length >= MAX_ATTACHMENTS}
              className='shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-300 dark:border-slate-600 text-slate-500 hover:text-clcpurple hover:border-clcpurple bg-white dark:bg-slate-800 disabled:opacity-40 disabled:hover:text-slate-500 disabled:hover:border-slate-300 transition-colors'
              aria-label='Attach a file'
              title={attachments.length >= MAX_ATTACHMENTS ? `Up to ${MAX_ATTACHMENTS} files per message` : 'Attach an image, audio or video'}
            >
              <PaperClipIcon className='h-5 w-5' />
            </button>
            {/* Not hidden with `hidden`: Safari won't open the picker for an
                input that isn't laid out. Off-screen and zero-sized works. */}
            <input
              ref={fileInputRef}
              type='file'
              accept={ALL_ACCEPTED.join(',')}
              multiple
              onChange={onFilePicked}
              className='sr-only'
              tabIndex={-1}
            />
            {/* The border, background and focus ring live on this wrapper so the
                highlight layer and the textarea share one box. A textarea can't
                style its own content, so mention pills in the composer have to
                be painted behind transparent text.
                Attachment previews sit inside the same border, above the text,
                so the composer grows into one box rather than sprouting a
                second one. */}
            <div
              onDragOver={onComposerDragOver}
              onDragLeave={() => setDragOver(false)}
              onDrop={onComposerDrop}
              className={`flex-1 min-w-0 rounded-lg border bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-clcpurple focus-within:border-clcpurple ${dragOver ? 'border-clcpurple ring-2 ring-clcpurple' : 'border-slate-300 dark:border-slate-600'}`}
            >
              {/* Sits inside the composer's border, above both the previews
                  and the text, so answering someone grows the same box rather
                  than stacking a separate strip on top of it. */}
              {replyTarget && (
                <div className='flex items-center gap-2 px-2.5 py-1.5 border-b border-slate-200 dark:border-slate-700'>
                  <svg viewBox='0 0 20 20' fill='none' className='h-3.5 w-3.5 shrink-0 text-slate-400' aria-hidden='true'>
                    <path d='M3 13V8a3 3 0 013-3h11' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
                    <path d='M14 2.5L17.5 5 14 7.5' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  <span className='min-w-0 truncate text-xs text-slate-500 dark:text-slate-400'>
                    Replying to{' '}
                    <span className='font-semibold text-slate-700 dark:text-slate-200'>
                      {replyTarget.user?.displayName || replyTarget.user?.username || 'someone'}
                    </span>
                  </span>
                  <button
                    type='button'
                    onClick={cancelReply}
                    className='ml-auto shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                    aria-label='Cancel reply'
                    title='Cancel reply'
                  >
                    <svg viewBox='0 0 20 20' fill='currentColor' className='h-3.5 w-3.5' aria-hidden='true'>
                      <path fillRule='evenodd' d='M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z' clipRule='evenodd' />
                    </svg>
                  </button>
                </div>
              )}
              {attachments.length > 0 && (
                <div className='flex flex-wrap gap-2 px-2 pt-2'>
                  {attachments.map(att => (
                    <div key={att.id} className='relative h-16 w-16 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700'>
                      {/* An image previews itself. Audio has nothing to show,
                          and a video frame needs the file decoded, so both get
                          an icon and their name instead. */}
                      {att.kind === 'image' ? (
                        <img
                          src={att.preview}
                          alt={att.name || ''}
                          className={`h-full w-full object-cover ${att.key ? '' : 'opacity-40'}`}
                        />
                      ) : (
                        <div className={`relative h-full w-full flex flex-col items-center justify-center px-1 text-slate-500 dark:text-slate-300 ${att.key ? '' : 'opacity-40'}`}>
                          {att.posterPreview && (
                            <img src={att.posterPreview} alt='' className='absolute inset-0 h-full w-full object-cover' />
                          )}
                          <span className={att.posterPreview ? 'relative text-white drop-shadow' : 'contents'}>
                            {att.kind === 'video'
                              ? <FilmIcon className='h-5 w-5' />
                              : <MusicNoteIcon className='h-5 w-5' />}
                          </span>
                          {!att.posterPreview && (
                            <span className='mt-0.5 w-full truncate text-center text-[9px] leading-tight'>{att.name}</span>
                          )}
                        </div>
                      )}
                      {!att.key && (
                        <div className='absolute inset-x-0 bottom-0'>
                          <div className='h-1 bg-black/30'>
                            <div className='h-full bg-clcpurple transition-all' style={{ width: `${att.progress}%` }} />
                          </div>
                        </div>
                      )}
                      <button
                        type='button'
                        onClick={() => dropAttachment(att.id)}
                        className='absolute top-0.5 right-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-black/60 text-white hover:bg-black/80'
                        aria-label={`Remove ${att.name || 'file'}`}
                        title='Remove'
                      >
                        <TrashIcon className='h-3 w-3' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className='relative'>
                <div
                  ref={overlayRef}
                  aria-hidden='true'
                  className={`${COMPOSER_BOX} absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-transparent pointer-events-none select-none`}
                >
                  {composerHighlightParts}
                </div>
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={onDraftChange}
                  onKeyDown={handleKeyDown}
                  onPaste={onComposerPaste}
                  onScroll={syncOverlayScroll}
                  onBlur={() => setTimeout(() => setAcOpen(false), 150)}
                  rows={1}
                  placeholder='Message…'
                  className={`${COMPOSER_BOX} relative block w-full resize-none bg-transparent focus:outline-none`}
                />
              </div>
            </div>
            <button
              type='submit'
              disabled={!canSend}
              className='shrink-0 inline-flex items-center px-4 py-2 rounded-lg bg-clcpurple text-white font-semibold text-sm hover:bg-clcpurple-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </form>
        </div>
      )}
    </div>
    {/* /main chat column */}

    {/* Desktop members sidebar */}
    <div className='hidden md:flex md:flex-col md:w-[260px] md:shrink-0 md:border-l md:border-slate-200 md:dark:border-slate-800 md:pl-4 md:pt-1 min-h-0'>
      <div className='flex items-baseline justify-between px-2 pb-3 border-b border-slate-100 dark:border-slate-800'>
        <h2 className='text-base font-bold text-slate-900 dark:text-slate-100'>Members</h2>
        <span className='text-xs text-slate-500'>{memberCount}</span>
      </div>
      <MembersSidebar members={members} remote={membersRemote} total={memberTotal} className='flex-1 pt-3' />
    </div>

    <MentionHelperModal
      open={mentionModalOpen}
      onClose={() => setMentionModalOpen(false)}
      specials={specials}
      members={members}
      remote={membersRemote}
      total={memberTotal}
      onSearch={searchMembers}
      onPick={insertTokenAtCaret}
    />

    <ReactionsModal
      open={!!reactionsModal}
      messageId={reactionsModal?.messageId}
      initialKey={reactionsModal?.key}
      onClose={closeReactionsModal}
      fetchReactors={fetchReactors}
      epoch={reactorsEpoch}
    />

    {/* Mobile drawer */}
    {membersDrawerOpen && (
      <div className='md:hidden fixed inset-0 z-50 flex'>
        <div
          className='absolute inset-0 bg-slate-900/50'
          onClick={() => setMembersDrawerOpen(false)}
        />
        <div className='relative ml-auto h-full w-[85%] max-w-xs bg-white dark:bg-slate-900 shadow-2xl flex flex-col p-4'>
          <div className='flex items-center justify-between mb-3'>
            <h2 className='text-base font-bold text-slate-900 dark:text-slate-100'>
              Members <span className='ml-1 text-xs font-normal text-slate-500'>{memberCount}</span>
            </h2>
            <button
              type='button'
              onClick={() => setMembersDrawerOpen(false)}
              className='p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800'
              aria-label='Close members list'
            >
              <svg viewBox='0 0 20 20' fill='currentColor' className='h-5 w-5' aria-hidden='true'>
                <path fillRule='evenodd' d='M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z' clipRule='evenodd' />
              </svg>
            </button>
          </div>
          <MembersSidebar members={members} remote={membersRemote} total={memberTotal} className='flex-1' />
        </div>
      </div>
    )}

    </div>
  )
}

export default CourseChat
